import type { PermanentVideoChannel, StableVideoChannelId, VideoLane } from "./videoChannels";

/** Stable provider identity. Slugs and names remain presentation metadata. */
export type VideoChannelIdentity = StableVideoChannelId;

export type VideoVerificationMode = "live" | "historical";

export type VideoPlaybackState =
  | "verified-playing"
  | "verified-not-playing"
  | "unknown"
  | "expired"
  | "insufficient-history";

/** Immutable channel fields captured when the roster identity was observed. */
export interface VideoChannelIdentityCapture {
  channelId: VideoChannelIdentity;
  provider: "youtube";
  youtubeChannelId: string;
  name: string;
  shortName: string;
  slug: string;
  description: string;
  region: string;
  categories: readonly VideoLane[];
  officialChannelUrl: string;
  sourceUrl: string;
  indexable: true;
  sourceCommit: string;
  capturedAt: string;
}

export interface VideoMediaTimeSample {
  sampledAt: string;
  /** Monotonic offset from observation start. Wall-clock jumps do not affect elapsed time. */
  elapsedMs: number;
  state: "playing" | "paused" | "buffering" | "unstarted" | "ended" | "error" | "unknown";
  mediaTimeSeconds: number | null;
  terminalError?: string | null;
}

/** Collector payload. `retainedAt` is assigned by the local importer. */
export interface VideoPlaybackObservation {
  channelId: VideoChannelIdentity;
  youtubeChannelId: string;
  sourceCommit: string;
  policyVersion: string;
  applicationOrigin: string;
  browserName: string;
  browserVersion: string;
  observerRegion: string | null;
  startedAt: string;
  endedAt: string;
  samples: readonly VideoMediaTimeSample[];
  evidenceReferences: readonly string[];
  /** Optional screenshots are retained as bytes, not as external paths. */
  evidenceArtifacts?: readonly {
    id: string;
    name: string;
    mediaType: string;
    base64Bytes: string;
  }[];
}

export interface VideoPlaybackCheckView {
  state: VideoPlaybackState;
  /** State of the retained observation before expiry was applied. */
  observedState: Exclude<VideoPlaybackState, "expired" | "insufficient-history"> | null;
  expired: boolean;
  receiptId: string | null;
  observationStartedAt: string | null;
  observationEndedAt: string | null;
  retainedAt: string | null;
  expiresAt: string | null;
  sourceCommit: string | null;
  policyVersion: string | null;
  applicationOrigin: string | null;
  browserName: string | null;
  browserVersion: string | null;
  observerRegion: string | null;
  mediaTimeAdvanceSeconds: number | null;
  terminalError: string | null;
  evidenceDigests: readonly string[];
  observationIdentity: {
    captureId: string;
    captureDigest: string;
    channelId: VideoChannelIdentity;
    capturedAt: string;
    sourceCommit: string;
    name: string;
    slug: string;
  } | null;
}

export interface VideoChannelVerificationView {
  channelId: VideoChannelIdentity;
  channel: Omit<PermanentVideoChannel, "lastVerifiedOn" | "resolutionMethod">;
  identityCaptureId: string | null;
  identityCaptureDigest: string | null;
  identityCapturedAt: string | null;
  identitySourceCommit: string | null;
  playback: VideoPlaybackCheckView;
}

export interface VideoVerificationView {
  schemaVersion: 1;
  mode: VideoVerificationMode;
  asOf: string;
  historicalMediaRetained: false;
  channels: readonly VideoChannelVerificationView[];
}

export interface GetVideoVerificationViewOptions {
  asOf?: string;
  channelId?: VideoChannelIdentity;
}

/** Stable, explicitly UTC-facing labels shared by API and presentation code. */
export function videoPlaybackStateLabel(state: VideoPlaybackState): string {
  switch (state) {
    case "verified-playing": return "Dated playback check passed";
    case "verified-not-playing": return "Dated playback check failed";
    case "expired": return "Dated playback check expired";
    case "insufficient-history": return "No retained check by this time";
    default: return "Playback state unknown";
  }
}

